import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import {
  IReferralProgram,
  IFetchProgramsRequest,
} from './ReferralProgram.types';

/**
 * Minimal reducer/action factory for "program list" screens (referral programs,
 * referrer conditions, joining bonus programs). Body mirrors
 * `ReferralProgram.reducer.ts` + `ReferralProgram.actions.ts`, but action types
 * are prefixed so two slices mounted in the same store never cross-talk, and
 * state is read from `getState()[stateKey]` instead of a hard-wired key.
 *
 * The existing referral/condition slices are intentionally left on their
 * unprefixed constants (migrating them is out of scope).
 */

type ProgramListDispatch = ThunkDispatch<unknown, unknown, AnyAction>;

export interface IProgramListApiResponse<T = unknown> {
  isSuccess: boolean;
  data?: T;
  errorMessage?: string;
}

export interface IFetchProgramsPagination {
  is_next_present?: boolean;
  last_row_id_prev?: string;
  last_row_id_next?: string;
  last_sorted_column_value_prev?: string | null;
  last_sorted_column_value_next?: string | null;
  result_per_page?: number;
}

export interface IFetchProgramsResponseData {
  page_data?: IReferralProgram[];
  count?: number;
  pagination?: IFetchProgramsPagination;
}

export interface IProgramListApi {
  fetch: (
    body: IFetchProgramsRequest,
  ) => Promise<IProgramListApiResponse<IFetchProgramsResponseData>>;
  fetchOne: (
    id: number,
  ) => Promise<IProgramListApiResponse<Partial<IReferralProgram>>>;
}

export interface IHubFilterOption {
  value: string | number;
  label?: string;
}

export interface IProgramListFilters {
  hubIds?: IHubFilterOption[];
  status?: string[];
  searchQuery?: string;
}

export interface IProgramListSortParams {
  sortBy: string;
  descendingOrder: boolean;
}

export interface IProgramListPagination {
  pageNumber: number;
  limit: number;
  lastRowIdPrev?: string;
  lastRowIdNext?: string;
  lastSortedColumnValuePrev?: string | null;
  lastSortedColumnValueNext?: string | null;
  resultPerPage?: number;
}

export interface IProgramListState {
  totalProgramsCount: number;
  filteredPrograms: IReferralProgram[];
  searchResults: unknown[];
  selectedProgram: IReferralProgram | null;
  fetchedCount: number;
  isNextPresent: boolean;
  isLoading: boolean;
  isSearchLoading: boolean;
  isFetchByIdLoading: boolean;
  isLoadingMore: boolean;
  errorMessage: string | null;
  searchErrorMessage: string | null;
  fetchByIdErrorMessage: string | null;
  appliedFilters: IProgramListFilters;
  sortParams: IProgramListSortParams;
  pagination: IProgramListPagination;
}

export interface IProgramListAction extends AnyAction {
  type: string;
  data?: any;
  error?: string;
  filters?: IProgramListFilters;
  sortParams?: Partial<IProgramListSortParams>;
  pagination?: Partial<IProgramListPagination>;
  isLoadMore?: boolean;
  fetchedCount?: number;
  isLoading?: boolean;
  totalProgramsCount?: number;
  isNextPresent?: boolean;
}

export interface IProgramListActionTypes {
  FETCH_PROGRAMS: string;
  FETCH_PROGRAMS_LOADING: string;
  FETCH_PROGRAMS_ERROR: string;
  FETCH_PROGRAM_BY_ID: string;
  FETCH_PROGRAM_BY_ID_LOADING: string;
  FETCH_PROGRAM_BY_ID_ERROR: string;
  SET_FILTERS: string;
  RESET_FILTERS: string;
  SET_SORT_PARAMS: string;
  SET_PAGINATION: string;
  RESET_PAGINATION: string;
}

type RootStateLike = Record<string, unknown>;

export const buildProgramListActionTypes = (
  prefix: string,
): IProgramListActionTypes => ({
  FETCH_PROGRAMS: `${prefix}/FETCH_PROGRAMS`,
  FETCH_PROGRAMS_LOADING: `${prefix}/FETCH_PROGRAMS_LOADING`,
  FETCH_PROGRAMS_ERROR: `${prefix}/FETCH_PROGRAMS_ERROR`,
  FETCH_PROGRAM_BY_ID: `${prefix}/FETCH_PROGRAM_BY_ID`,
  FETCH_PROGRAM_BY_ID_LOADING: `${prefix}/FETCH_PROGRAM_BY_ID_LOADING`,
  FETCH_PROGRAM_BY_ID_ERROR: `${prefix}/FETCH_PROGRAM_BY_ID_ERROR`,
  SET_FILTERS: `${prefix}/SET_FILTERS`,
  RESET_FILTERS: `${prefix}/RESET_FILTERS`,
  SET_SORT_PARAMS: `${prefix}/SET_SORT_PARAMS`,
  SET_PAGINATION: `${prefix}/SET_PAGINATION`,
  RESET_PAGINATION: `${prefix}/RESET_PAGINATION`,
});

export const programListDefaultState: IProgramListState = {
  totalProgramsCount: 0,
  filteredPrograms: [],
  searchResults: [],
  selectedProgram: null,
  isLoading: false,
  isLoadingMore: false,
  isSearchLoading: false,
  isFetchByIdLoading: false,
  errorMessage: null,
  searchErrorMessage: null,
  fetchByIdErrorMessage: null,
  appliedFilters: {},
  sortParams: {
    sortBy: 'created_at',
    descendingOrder: true,
  },
  pagination: {
    pageNumber: 1,
    limit: 20,
  },
  fetchedCount: 0,
  isNextPresent: false,
};

export const createProgramListSlice = (
  prefix: string,
  api: IProgramListApi,
  stateKey: string,
) => {
  const actionTypes = buildProgramListActionTypes(prefix);

  const reducer = (
    state: IProgramListState = programListDefaultState,
    action: IProgramListAction,
  ): IProgramListState => {
    switch (action.type) {
      case actionTypes.FETCH_PROGRAMS:
        return {
          ...state,
          totalProgramsCount:
            action.totalProgramsCount ?? state.totalProgramsCount,
          filteredPrograms: action.isLoadMore
            ? [...state.filteredPrograms, ...action.data]
            : action.data,
          errorMessage: null,
          fetchedCount: action.fetchedCount || 0,
          isNextPresent: action.isNextPresent ?? false,
          pagination: {
            ...state.pagination,
            ...(action.pagination || {}),
          },
        };

      case actionTypes.FETCH_PROGRAMS_LOADING:
        return {
          ...state,
          isLoading: action.isLoadMore ? false : action.data,
          isLoadingMore: action.isLoadMore ? action.data : false,
        };

      case actionTypes.FETCH_PROGRAMS_ERROR:
        return { ...state, errorMessage: action.error };

      case actionTypes.FETCH_PROGRAM_BY_ID:
        return {
          ...state,
          selectedProgram: action.data,
          fetchByIdErrorMessage: null,
        };

      case actionTypes.FETCH_PROGRAM_BY_ID_LOADING:
        return {
          ...state,
          isFetchByIdLoading: action.isLoading,
          selectedProgram: action.data || state.selectedProgram,
        };

      case actionTypes.FETCH_PROGRAM_BY_ID_ERROR:
        return { ...state, fetchByIdErrorMessage: action.error };

      case actionTypes.SET_FILTERS:
        return { ...state, appliedFilters: action.filters };

      case actionTypes.RESET_FILTERS:
        return { ...state, appliedFilters: {} };

      case actionTypes.SET_SORT_PARAMS:
        return {
          ...state,
          sortParams: { ...state.sortParams, ...action.sortParams },
        };

      case actionTypes.SET_PAGINATION:
        return {
          ...state,
          pagination: { ...state.pagination, ...action.pagination },
        };

      case actionTypes.RESET_PAGINATION:
        return { ...state, pagination: programListDefaultState.pagination };

      default:
        return state;
    }
  };

  const getSliceState = (getState: () => RootStateLike): IProgramListState =>
    (getState()[stateKey] as IProgramListState) || programListDefaultState;

  const fetchProgramById =
    (id: number) => async (dispatch: ProgramListDispatch) => {
      dispatch({
        type: actionTypes.FETCH_PROGRAM_BY_ID_LOADING,
        isLoading: true,
        data: { id },
      });

      const response = await api.fetchOne(id);

      if (response.isSuccess) {
        dispatch({
          type: actionTypes.FETCH_PROGRAM_BY_ID,
          data: {
            id,
            ...response.data,
          },
        });
      } else {
        dispatch({
          type: actionTypes.FETCH_PROGRAM_BY_ID_ERROR,
          error: response.errorMessage,
        });
      }

      dispatch({
        type: actionTypes.FETCH_PROGRAM_BY_ID_LOADING,
        isLoading: false,
      });
    };

  const fetchPrograms =
    ({ isLoadMore = false }: { isLoadMore?: boolean } = {}) =>
    async (dispatch: ProgramListDispatch, getState: () => RootStateLike) => {
      dispatch({
        type: actionTypes.FETCH_PROGRAMS_LOADING,
        data: true,
        isLoadMore,
      });

      const {
        appliedFilters = {},
        sortParams,
        pagination,
      } = getSliceState(getState);
      const hubIds = appliedFilters?.hubIds;
      const status = appliedFilters?.status;
      const searchQuery = appliedFilters?.searchQuery;

      const sortBy = sortParams?.sortBy;
      const descendingOrder = sortParams?.descendingOrder;

      const pageNumber = pagination?.pageNumber;
      const limit = pagination?.limit;

      const isFiltersApplied = Boolean(
        hubIds?.length || status?.length || searchQuery,
      );

      const requestBody: IFetchProgramsRequest = {
        sort: {
          sort_by: sortBy,
          descending_order: descendingOrder,
        },
        filters: {
          q: searchQuery,
          status_list: status,
          hub_id_list: hubIds?.map((item) => item.value) || [],
        },
        pagination: {
          next_or_prev: isLoadMore ? 'next' : 'first',
          result_per_page: limit,
          last_row_id: isLoadMore
            ? pagination?.lastRowIdNext
            : pagination?.lastRowIdPrev,
          last_sorted_column_value: isLoadMore
            ? pagination?.lastSortedColumnValueNext
            : pagination?.lastSortedColumnValuePrev,
        },
      };

      const response = await api.fetch(requestBody);

      if (response.isSuccess) {
        const listData = response.data;
        const responsePagination = listData?.pagination;

        dispatch({
          type: actionTypes.FETCH_PROGRAMS,
          data: listData?.page_data || [],
          isLoadMore,
          fetchedCount: listData?.count || 0,
          isNextPresent: responsePagination?.is_next_present || false,
          pagination: {
            lastRowIdPrev: responsePagination?.last_row_id_prev,
            lastRowIdNext: responsePagination?.last_row_id_next,
            lastSortedColumnValuePrev:
              responsePagination?.last_sorted_column_value_prev,
            lastSortedColumnValueNext:
              responsePagination?.last_sorted_column_value_next,
            resultPerPage: responsePagination?.result_per_page,
          },
          ...(!isFiltersApplied
            ? { totalProgramsCount: listData?.count || 0 }
            : {}),
        });
      } else {
        dispatch({
          type: actionTypes.FETCH_PROGRAMS_ERROR,
          error: response.errorMessage,
        });
      }

      dispatch({
        type: actionTypes.FETCH_PROGRAMS_LOADING,
        data: false,
        isLoadMore,
      });

      // Fetch details of first program
      if (!isLoadMore && pageNumber === 1) {
        if (response.isSuccess && response.data?.page_data?.length) {
          dispatch(fetchProgramById(response.data.page_data[0].id));
        } else {
          dispatch({ type: actionTypes.FETCH_PROGRAM_BY_ID }); // Clear selected program
        }
      }
    };

  const resetPagination = () => (dispatch: ProgramListDispatch) => {
    dispatch({ type: actionTypes.RESET_PAGINATION });
    dispatch(fetchPrograms());
  };

  const applyFilters =
    (filters: IProgramListFilters) => (dispatch: ProgramListDispatch) => {
      dispatch({ type: actionTypes.SET_FILTERS, filters });
      dispatch(resetPagination());
    };

  const setSortParams =
    (sortParams: Partial<IProgramListSortParams>) =>
    (dispatch: ProgramListDispatch) => {
      dispatch({ type: actionTypes.SET_SORT_PARAMS, sortParams });
      dispatch(resetPagination());
    };

  const setPagination =
    (pagination: IProgramListPagination) =>
    (dispatch: ProgramListDispatch, getState: () => RootStateLike) => {
      const { pagination: currentPagination } = getSliceState(getState);
      const isLoadMore = pagination.pageNumber > currentPagination.pageNumber;

      dispatch({ type: actionTypes.SET_PAGINATION, pagination });
      dispatch(fetchPrograms({ isLoadMore }));
    };

  const resetFilters = () => (dispatch: ProgramListDispatch) => {
    dispatch({ type: actionTypes.RESET_FILTERS });
    dispatch({ type: actionTypes.RESET_PAGINATION });
    dispatch(fetchPrograms());
  };

  return {
    reducer,
    actionTypes,
    stateKey,
    actions: {
      fetchPrograms,
      fetchProgramById,
      applyFilters,
      setSortParams,
      setPagination,
      resetPagination,
      resetFilters,
    },
  };
};

export type ProgramListSlice = ReturnType<typeof createProgramListSlice>;
